import { FormattedMessage, useIntl } from 'react-intl'
import { rem } from 'polished'
import styled from 'styled-components'

import BodyText from 'LuxKit/Typography/BodyText'
import TextButton from 'LuxKit/Button/TextButton'
import IconButton from 'LuxKit/Button/IconButton'
import LineAngleLeftIcon from 'LuxKit/Icons/line/LineAngleLeftIcon'
import LineAngleRightIcon from 'LuxKit/Icons/line/LineAngleRightIcon'
import Group from 'components/utils/Group'
import Sheet from 'components/utils/Sheet'
import zIndex from 'styles/tools/z-index'
import { useStaffRewind } from 'tripPlanner/assistant/contexts/StaffRewindContext'

const ControlsRow = styled(Group)`
  width: fit-content;
  margin-top: ${rem(-4)};
`

const Banner = styled(Sheet)`
  position: sticky;
  top: 0;
  z-index: ${zIndex.stickyHeaderBanner};
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${rem(12)};
  margin-bottom: ${rem(8)};
  box-shadow: 0 ${rem(10)} ${rem(14)} ${rem(-14)} rgba(20, 24, 35, 0.18);
`

const BannerMessage = styled(BodyText)`
  min-width: 0;
`

// The arrows point in reading order (back = start edge), so mirror them under RTL to keep
// "back in time" on the leading side.
const StepperArrows = styled(Group)`
  [dir="rtl"] & {
    flex-direction: row-reverse;
  }
`

/**
 * Per-assistant-message staff affordances (rewind / "chat time machine"):
 *  - "View itinerary here" toggles a read-only snapshot of the itinerary at this turn into
 *    the trip panel;
 *  - "Copy trip from here" forks the trip at this turn into a new staff-owned chat.
 *
 * Renders nothing unless the viewer is staff looking at another user's trip (canStaffRewind).
 * Safe to mount anywhere: the StaffRewind context has a non-throwing default.
 */
export function StaffMessageRewindControls({ messageId }: { messageId: string }) {
  const intl = useIntl()
  const { canStaffRewind, inspectedMessageId, isInspectLoading, inspect, fork, forkingMessageId } = useStaffRewind()

  if (!canStaffRewind) {
    return null
  }

  const isInspectingThis = inspectedMessageId === messageId
  const isForkingThis = forkingMessageId === messageId

  const inspectLabel = isInspectingThis
    ? isInspectLoading
      ? intl.formatMessage({
          id: 'Hg89lbemO9W7u_BR4nMm',
          defaultMessage: 'Loading…',
          description: 'Staff rewind: label while the historical itinerary snapshot is loading',
        })
      : intl.formatMessage({
          id: 'oiow9LBq2ATF0wPEUlVE',
          defaultMessage: 'Viewing itinerary here',
          description: 'Staff rewind: label on the active button while inspecting the itinerary at this message',
        })
    : intl.formatMessage({
        id: '0Dbms2p0JrDvs67Yn4db',
        defaultMessage: 'View itinerary here',
        description: 'Staff rewind: button to view the itinerary as it stood at this message',
      })

  const forkLabel = isForkingThis
    ? intl.formatMessage({
        id: 'XXzYW3xx5lrAbGqBrGs2',
        defaultMessage: 'Copying…',
        description: 'Staff rewind: label while copying the trip into a new chat',
      })
    : intl.formatMessage({
        id: 'pqFF6axDhsJKDHVj2yau',
        defaultMessage: 'Copy trip from here',
        description: 'Staff rewind: button to copy the trip at this message into a new staff-owned chat',
      })

  return (
    <ControlsRow direction="horizontal" gap={8} data-staff-rewind-controls>
      <TextButton
        kind={isInspectingThis ? 'primary' : 'tertiary'}
        size="small"
        onClick={() => inspect(messageId)}
      >
        {inspectLabel}
      </TextButton>
      <TextButton
        kind="tertiary"
        size="small"
        disabled={forkingMessageId !== undefined}
        onClick={() => fork(messageId)}
      >
        {forkLabel}
      </TextButton>
    </ControlsRow>
  )
}

/**
 * Staff-only "rewind" stepper. Always shown while a staff member views another user's trip,
 * so they can walk the itinerary back and forth through each assistant turn using the arrows.
 * Defaults to the live/newest state; the back arrow steps into history and the panel
 * time-travels to the itinerary as it stood at that turn. When a past state is on screen the
 * banner switches to an alert treatment so it's obvious the panel is not live.
 */
export function StaffRewindBanner() {
  const intl = useIntl()
  const {
    canStaffRewind,
    isInspecting,
    isInspectLoading,
    exitInspect,
    stateCount,
    currentStatePosition,
    stepBack,
    stepForward,
    canStepBack,
    canStepForward,
  } = useStaffRewind()

  // Nothing to step through until at least one assistant turn exists.
  if (!canStaffRewind || stateCount === 0) {
    return null
  }

  const backLabel = intl.formatMessage({
    id: 'OJUpFVJhcq6orP5BUHML',
    defaultMessage: 'View the previous trip state',
    description: 'Staff rewind: accessible label for the back arrow that steps to the previous assistant turn',
  })
  const forwardLabel = intl.formatMessage({
    id: 'CWunSPPPBBtrU5KAvmhT',
    defaultMessage: 'View the next trip state',
    description: 'Staff rewind: accessible label for the forward arrow that steps to the next assistant turn',
  })

  return (
    <Banner
      className="no-print"
      background={isInspecting ? 'critical' : 'neutral-seven'}
      paletteType={isInspecting ? 'inverse' : 'default'}
      padding={8}
    >
      <Group direction="horizontal" gap={8} verticalAlign="center">
        <StepperArrows direction="horizontal" gap={4} verticalAlign="center">
          <IconButton
            kind="tertiary"
            size="small"
            shape="circle"
            aria-label={backLabel}
            disabled={!canStepBack}
            onClick={stepBack}
          >
            <LineAngleLeftIcon />
          </IconButton>
          <IconButton
            kind="tertiary"
            size="small"
            shape="circle"
            aria-label={forwardLabel}
            disabled={!canStepForward}
            onClick={stepForward}
          >
            <LineAngleRightIcon />
          </IconButton>
        </StepperArrows>
        <BannerMessage variant="medium" weight="bold" colour={isInspecting ? 'normal-contrast' : 'neutral-one'}>
          {isInspectLoading ? (
            <FormattedMessage
              id="Hg89lbemO9W7u_BR4nMm"
              defaultMessage="Loading…"
              description="Staff rewind: label while the historical itinerary snapshot is loading"
            />
          ) : (
            <FormattedMessage
              id="j9v-NRAw89Vg8PCmbfLX"
              defaultMessage="Message {position} of {total}"
              description="Staff rewind banner label showing which assistant message is on screen in the chat time machine"
              values={{ position: currentStatePosition, total: stateCount }}
            />
          )}
        </BannerMessage>
      </Group>
      {isInspecting && (
        <TextButton kind="tertiary" size="small" onClick={exitInspect}>
          <FormattedMessage
            id="eTnpR6ZsWmIz0h4et6b3"
            defaultMessage="Return to latest"
            description="Staff rewind: button to exit the historical itinerary view and return to the live itinerary"
          />
        </TextButton>
      )}
    </Banner>
  )
}
